import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import i18n from './locales/i18n';

// Components
import Spinner from './components/Spinner';
import PublicLayout from './components/PublicLayout';
import QuotePopup from './components/QuotePopup';

// Pages
import Home from './pages/HomeNew';
import Contact from './pages/Contact';
import Work from './pages/Work';
import Admin from './pages/Admin';
import NotFound from './pages/NotFound';
import CacheRefreshManual from './pages/CacheRefreshManual';

// Providers
import { DataProvider } from './providers/DataProvider';
import { ColorModeProvider } from './providers/ColorModeProvider';
import { NotificationProvider, useNotification } from './providers/NotificationProvider';
import { CursorProvider } from './providers/CursorProvider';
import { BackgroundColorProvider } from './providers/BackgroundColorProvider';
import CustomCursor from './components/CustomCursor';

import { useQuotePopup } from './hooks/useQuotePopup';

// Inner component that uses the notification hook
const AppContent: React.FC = () => {
  const { isVisible, hidePopup, settings: popupSettings } = useQuotePopup();

  const { showError: _showError } = useNotification();

  return (
    <div className="App">
      <CustomCursor />
      <BrowserRouter>
        <DataProvider>
          <PublicLayout>
            <Suspense fallback={<Spinner />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/work" element={<Work />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/cache-refresh" element={<CacheRefreshManual />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PublicLayout>
        </DataProvider>

        {/* Quote Popup */}
        <QuotePopup
          open={isVisible}
          onClose={hidePopup}
          settings={popupSettings}
        />

        {/* Chat Button */}
        {/* <ChatButton onClick={handleChatButtonClick} /> */}
      </BrowserRouter>
    </div>
  );
};

// Main App component with all providers
function App() {
  return (
    <ColorModeProvider>
      <CssBaseline />
      <I18nextProvider i18n={i18n}>
        <NotificationProvider>
          <CursorProvider>
            <BackgroundColorProvider>
              <AppContent />
            </BackgroundColorProvider>
          </CursorProvider>
        </NotificationProvider>
      </I18nextProvider>
    </ColorModeProvider>
  );
}

export default App;
